import { parseDate } from '@internationalized/date';
import { observer } from 'mobx-react';
import { Button, DateInput, DateRangePicker, DateSegment, Group, Label } from 'react-aria-components/DateRangePicker';
import { I18nProvider } from 'react-aria-components/I18nProvider';
import { Popover } from 'react-aria-components/Popover';
import {
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarHeading,
  RangeCalendar,
} from 'react-aria-components/RangeCalendar';

import Icon from '../Icon/Icon';
import css from './DateRange.css';

interface DateRangeProps {
  startDate: string | undefined;
  endDate: string | undefined;
  setDateRange: (startDate: string | undefined, endDate: string | undefined) => void;
  dateLabel?: string;
}

function DateRange({ dateLabel, endDate, setDateRange, startDate }: DateRangeProps) {
  const start = startDate ? parseDate(startDate) : null;
  const end = endDate ? parseDate(endDate) : null;
  const value = start && end ? { start, end } : null;
  const visibleMonths = 2;

  return (
    // Force day-month-year ordering across every market. RAC derives segment order from the locale.
    <I18nProvider locale="en-GB">
      <DateRangePicker value={value} onChange={(range) => setDateRange(range?.start.toString(), range?.end.toString())}>
        <Label className={css.label}>{dateLabel}</Label>
        <Group className={css.group}>
          <Button className={css.calendarButton}>
            <Icon name="calendar" />
          </Button>
          <DateInput slot="start" className={css.input}>
            {(segment) => <DateSegment segment={segment} className={css.segment} />}
          </DateInput>
          <span className={css.separator} aria-hidden={true}>
            -
          </span>
          <DateInput slot="end" className={css.input}>
            {(segment) => <DateSegment segment={segment} className={css.segment} />}
          </DateInput>
        </Group>
        <Popover className={css.popover}>
          <RangeCalendar className={css.calendar} visibleDuration={{ months: visibleMonths }}>
            {Array.from({ length: visibleMonths }, (_, i) => (
              <div key={i}>
                <header className={css.calendarHeader}>
                  {i === 0 && (
                    <Button slot="previous" className={css.navButton}>
                      <Icon name="angleLeft" />
                    </Button>
                  )}
                  <CalendarHeading className={css.calendarHeading} offset={{ months: i }} />
                  {i === visibleMonths - 1 && (
                    <Button slot="next" className={css.navButton}>
                      <Icon name="angleRight" />
                    </Button>
                  )}
                </header>
                <CalendarGrid className={css.grid} offset={{ months: i }} weekdayStyle="short">
                  <CalendarGridHeader>
                    {(day) => <CalendarHeaderCell className={css.headerCell}>{day}</CalendarHeaderCell>}
                  </CalendarGridHeader>
                  <CalendarGridBody>{(date) => <CalendarCell className={css.cell} date={date} />}</CalendarGridBody>
                </CalendarGrid>
              </div>
            ))}
          </RangeCalendar>
        </Popover>
      </DateRangePicker>
    </I18nProvider>
  );
}

export default observer(DateRange);
